'use strict';

/**
 * Desktop locale dropdown: same contract as mobileCountrySelector.js → Page-SetLocale.
 * Lavazza uses .dd / .dd__menu (not base .navbar-header / .dropdown-menu).
 *
 * Do NOT use base keyboardAccessibility here: it runs preFunction on every keydown (including
 * Enter/Space), which opens the menu before the button's default action fires a click — click
 * then toggles closed (open → flash close).
 *
 * Arrow Up/Down must move focus between option links using list indices — DOM .next()/.prev()
 * on <a> does not reach the next locale (separators / different DOM shape).
 *
 * When the menu is closed, option links keep tabindex="-1" so Tab does not enter invisible
 * items (menu is only visually hidden via opacity, not display:none in the utility bar).
 */
const OPTION_LINKS_SELECTOR = '.dropdown-country-selector .country-selector__option a';

// Wires keyboard support, tabindex rules, MutationObserver, and focus-out closing for each instance.
module.exports = function initCountrySelector() {
	// Keeps locale links in or out of the tab order: when the menu is closed they use tabindex="-1"
	// so Tab skips invisible items; when open, tabindex is removed so links behave as normal anchors.
	function syncCountryOptionsTabIndex($dd) {
		const open = $dd.hasClass('is-open');
		$dd.find(OPTION_LINKS_SELECTOR).each(function syncOptionTabIndex() {
			if (open) {
				$(this).removeAttr('tabindex');
			} else {
				$(this).attr('tabindex', '-1');
			}
		});
	}

	// Collapses the dropdown, syncs ARIA on the toggle, and updates option tabindex for a closed menu.
	function closeCountryDropdown($dd) {
		$dd.removeClass('show is-open');
		$dd.find('.dd__toggle').attr('aria-expanded', 'false');
		$dd.find('.dd__menu').removeClass('show');
		syncCountryOptionsTabIndex($dd);
	}

	// Ensures the panel is marked open (classes + aria-expanded), refreshes option tabindex, and returns
	// the jQuery collection of option anchors for arrow-key focus moves.
	function ensureOpenAndOptions($dd) {
		if (!$dd.hasClass('show')) {
			$dd.addClass('show is-open');
			$dd.find('.dd__toggle').attr('aria-expanded', 'true');
		}
		syncCountryOptionsTabIndex($dd);
		return $dd.find(OPTION_LINKS_SELECTOR);
	}

	// Runs an initial tabindex sync and watches the root .dd for class changes (e.g. click outside /
	// dropdown.js) so option links stay in sync with is-open without duplicating every close path.
	function observeCountryDropdown($dd) {
		const el = $dd[0];
		if (!el || el._lvzCountryDdObserved) {
			return;
		}
		el._lvzCountryDdObserved = true;
		syncCountryOptionsTabIndex($dd);
		const obs = new MutationObserver(function onCountryDropdownClassChange() {
			syncCountryOptionsTabIndex($dd);
		});
		obs.observe(el, { attributes: true, attributeFilter: ['class'] });
	}

	function handleTabKey(e, $dd, $toggle) {
		if (!$dd.hasClass('is-open')) {
			return;
		}

		const $optionLinks = $dd.find(OPTION_LINKS_SELECTOR);
		const activeEl = document.activeElement;

		if ($toggle.is(activeEl)) {
			e.preventDefault();
			if ($optionLinks.length) {
				$optionLinks.first().focus();
			}
			return;
		}

		if ($optionLinks.index(activeEl) === -1) {
			closeCountryDropdown($dd);
		}
	}

	function handleArrowDown($countryOptions, $toggle, activeEl) {
		if ($toggle.is(activeEl)) {
			$countryOptions.first().focus();
			return;
		}
		const idxDown = $countryOptions.index(activeEl);
		if (idxDown !== -1 && idxDown < $countryOptions.length - 1) {
			$countryOptions.eq(idxDown + 1).focus();
		}
	}

	function handleArrowUp($dd, $countryOptions, $toggle, activeEl) {
		const idxUp = $countryOptions.index(activeEl);
		if (idxUp === 0) {
			closeCountryDropdown($dd);
			$toggle.focus();
			return;
		}
		if (idxUp > 0) {
			$countryOptions.eq(idxUp - 1).focus();
			return;
		}
		if ($toggle.is(activeEl)) {
			closeCountryDropdown($dd);
		}
	}

	// Delegated keydown: Tab moves from toggle into the first option when open; Escape closes; Arrow Up/Down
	// walk options (DOM siblings are not usable because of separators between <li> blocks).
	$(document).on('keydown', '.js-desktop-country-selector, .country-selector', function onCountrySelectorKeydown(e) {
		const $dd = $(this);
		const $toggle = $dd.find('.dd__toggle');

		if (e.key === 'Tab') {
			handleTabKey(e, $dd, $toggle);
			return;
		}

		if (e.key === 'Escape') {
			if ($dd.hasClass('is-open')) {
				e.preventDefault();
				closeCountryDropdown($dd);
				$toggle.focus();
			}
			return;
		}

		if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
			return;
		}

		e.preventDefault();

		const $countryOptions = ensureOpenAndOptions($dd);
		const activeEl = document.activeElement;

		if (e.key === 'ArrowDown') {
			handleArrowDown($countryOptions, $toggle, activeEl);
		} else {
			handleArrowUp($dd, $countryOptions, $toggle, activeEl);
		}
	});

	// Initialise each instance on load: observer + closed-state tabindex for option links.
	$('.js-desktop-country-selector, .country-selector').each(function initCountryDropdown() {
		observeCountryDropdown($(this));
	});

	// After dropdown.js toggles open/closed on click, re-sync tabindex on the next frame so Tab targets
	// match the new is-open state before the user presses Tab.
	$(document).on('click', '.js-desktop-country-selector .dd__toggle, .country-selector .dd__toggle', function onCountryToggleClick() {
		const $dd = $(this).closest('.js-desktop-country-selector, .country-selector');
		// Runs after dropdown.js has applied is-open so tabindex matches the updated state.
		window.requestAnimationFrame(function syncAfterToggle() {
			syncCountryOptionsTabIndex($dd);
		});
	});

	// When focus leaves the widget entirely (e.g. Tab past the last option), close the menu on the
	// next tick so relatedTarget / activeElement reflect the real focus target.
	$(document).on('focusout', '.js-desktop-country-selector, .country-selector', function onCountrySelectorFocusout() {
		const $dd = $(this);
		// Deferred check: focus may not have settled on the new target during the focusout event.
		window.setTimeout(function closeIfFocusLeft() {
			if (!$dd.hasClass('is-open')) {
				return;
			}
			const active = document.activeElement;
			if (active && $dd[0] && $dd[0].contains(active)) {
				return;
			}
			closeCountryDropdown($dd);
		}, 0);
	});
};
